import { useCallback, useMemo } from 'react';
import { memberCreateAPISchema } from '@hero/api';
import { Member, getObjectId } from '@hero/schema';
import { useFormUtil, UseFormOptions } from '../utils';
import { z } from 'zod';

export type MemberFormValues = z.infer<typeof memberCreateAPISchema>;

type UseMemberFormOptions = UseFormOptions<MemberFormValues> & {
  isEdit?: boolean;
};

export type UseMemberFormHook = ReturnType<typeof useMemberForm>;

export function useMemberForm(options?: UseMemberFormOptions) {
  const isEdit = options?.isEdit ?? false;

  const getDefaultValues = (
    values?: Partial<MemberFormValues>,
  ): Partial<MemberFormValues> => ({
    type: 'dispatcher',
    name: '',
    phone: '',
    email: '',
    driver: undefined,
    ...values,
  });

  const getResetValues = () => getDefaultValues();

  function memberToFormValues(member: Member): Partial<MemberFormValues> {
    // Every `driver.*` field is optional now — only `name` + `phone`
    // are required on the parent Member. Map undefined → undefined
    // instead of falling back to '' so the relaxed `nameSchema.optional()`
    // and `idSchema.optional()` validators don't reject empty strings.
    return {
      type: member.type,
      name: member.name,
      phone: member.phone,
      email: member.email ?? '',
      driver: member.driver
        ? {
            fullName: member.driver.fullName,
            nationality: member.driver.nationality,
            id: member.driver.id,
            idIssueNumber: member.driver.idIssueNumber,
            driverLicenseImage: member.driver.driverLicenseImage,
            idImage: member.driver.idImage,
          }
        : undefined,
    };
  }

  const defaultValues = useMemo(
    () => getDefaultValues(options?.defaultValues),
    [options?.defaultValues],
  );

  const form = useFormUtil({
    zodSchema: memberCreateAPISchema,
    ...options,
    defaultValues,
    getResetValues,
  });

  const setMember = useCallback(
    (member: Member) => {
      form.reset(getDefaultValues(memberToFormValues(member)));
    },
    [form.reset],
  );

  return {
    ...form,
    setMember,
    isEdit,
  };
}
